import { useEffect } from "react";
import Media from "./Media";

/**
 * LIGHTBOX — fullscreen viewer for a project's images and videos
 * ---------------------------------------------------------------------------
 * Opens over the page when a picture is clicked.
 *   ← / →   previous / next (also the arrow buttons)
 *   Esc     close (also the × button, or clicking the background)
 *
 * @param {object}   props
 * @param {Array}    props.items     media() objects that can be viewed
 * @param {number}   props.index     which one is showing
 * @param {function} props.onChange  called with the new index
 * @param {function} props.onClose
 */
export default function Lightbox({ items, index, onChange, onClose }) {
  const count = items.length;
  const go = (step) => onChange((index + step + count) % count);

  // Keyboard controls + stop the page scrolling underneath
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onChange((index + 1) % count);
      if (e.key === "ArrowLeft") onChange((index - 1 + count) % count);
    };
    window.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  }, [index, count, onChange, onClose]);

  const item = items[index];

  return (
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      onClick={onClose} // clicking the background closes it
    >
      {/* `key` restarts the fade-in when you move to another picture */}
      <div
        className="lightbox__stage"
        key={index}
        // Clicking the picture itself shouldn't close the viewer; clicking
        // the empty space around it should.
        onClick={(e) => e.target !== e.currentTarget && e.stopPropagation()}
      >
        <Media item={item} className="lightbox__media" playing eager />
      </div>

      <button
        type="button"
        className="lightbox__close"
        aria-label="Close"
        onClick={onClose}
      >
        ×
      </button>

      {count > 1 && (
        <>
          <button
            type="button"
            className="lightbox__nav lightbox__nav--prev"
            aria-label="Previous"
            onClick={(e) => {
              e.stopPropagation();
              go(-1);
            }}
          >
            ←
          </button>
          <button
            type="button"
            className="lightbox__nav lightbox__nav--next"
            aria-label="Next"
            onClick={(e) => {
              e.stopPropagation();
              go(1);
            }}
          >
            →
          </button>
          <span className="lightbox__count mono">
            {index + 1} / {count}
          </span>
        </>
      )}
    </div>
  );
}
